import React, { useRef, useEffect, useState } from 'react';
import '../styles/TrendChart.css';

interface DataPoint {
  date: string;
  value: number;
}

interface TrendChartProps {
  data: DataPoint[];
  color: string;
  unit?: string;
  thresholdValue?: number;
  thresholdLabel?: string;
}

const TrendChart: React.FC<TrendChartProps> = ({ 
  data, 
  color, 
  unit = '', 
  thresholdValue,
  thresholdLabel = '阈值'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [animationProgress, setAnimationProgress] = useState(0);

  useEffect(() => {
    // 简化动画进度
    const animationDuration = 800;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      setAnimationProgress(progress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [data]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const padding = { top: 25, right: 20, bottom: 30, left: 20 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (data.length === 0) return;

    // Calculate data range
    const values = data.map(d => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    
    // 如果有阈值，将其包含在计算范围内
    const allValues = thresholdValue ? [...values, thresholdValue] : values;
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    const valueRange = dataMax - dataMin || 1;
    const yPadding = valueRange * 0.15;

    // Draw lighter background
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, width, height);

    // Draw minimalist grid lines
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.03)';
    ctx.lineWidth = 1;
    
    for (let i = 1; i <= 2; i++) {
      const y = padding.top + chartHeight * i / 3;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }

    // 绘制阈值线（虚线）
    if (thresholdValue !== undefined && animationProgress > 0) {
      const thresholdY = height - padding.bottom - chartHeight * 
        (thresholdValue - dataMin + yPadding) / (valueRange + 2 * yPadding);
      
      ctx.strokeStyle = '#FF6B6B';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.globalAlpha = 0.8;
      
      ctx.beginPath();
      ctx.moveTo(padding.left, thresholdY);
      ctx.lineTo(width - padding.right, thresholdY);
      ctx.stroke();
      
      // 阈值标签
      ctx.fillStyle = '#FF6B6B';
      ctx.font = '600 10px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(thresholdLabel, width - padding.right - 8, thresholdY - 4);
      
      ctx.setLineDash([]); // 重置虚线
      ctx.globalAlpha = 1;
    }

    // Create curve path points
    const points = data.map((point, index) => {
      const x = padding.left + chartWidth * index / (data.length - 1);
      const y = height - padding.bottom - chartHeight * 
        (point.value - dataMin + yPadding) / (valueRange + 2 * yPadding);
      
      // 判断是否异常（超过阈值）
      const isAbnormal = thresholdValue !== undefined && point.value > thresholdValue;
      const isSafe = thresholdValue !== undefined && point.value <= thresholdValue * 0.9; // 安全值为阈值的90%以下
      
      return { 
        x, 
        y, 
        value: point.value, 
        date: point.date,
        isAbnormal,
        isSafe
      };
    });

    // Draw simplified area fill
    if (points.length > 1 && animationProgress > 0) {
      const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom);
      gradient.addColorStop(0, `${color}08`);
      gradient.addColorStop(1, `${color}02`);
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      
      ctx.moveTo(points[0].x, height - padding.bottom);
      
      const animatedLength = Math.floor((points.length - 1) * animationProgress) + 1;
      points.slice(0, animatedLength).forEach((point, index) => {
        if (index === 0) {
          ctx.lineTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });
      
      const lastPoint = points[Math.floor((points.length - 1) * animationProgress)];
      ctx.lineTo(lastPoint.x, height - padding.bottom);
      ctx.closePath();
      ctx.fill();
    }

    // Draw clean curve - 根据异常状态分段绘制不同颜色
    if (points.length > 0 && animationProgress > 0) {
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      const animatedLength = Math.floor((points.length - 1) * animationProgress) + 1;
      const visiblePoints = points.slice(0, animatedLength);
      
      // 绘制线段，根据状态使用不同颜色
      for (let i = 0; i < visiblePoints.length - 1; i++) {
        const currentPoint = visiblePoints[i];
        const nextPoint = visiblePoints[i + 1];
        
        // 根据当前点的状态选择颜色
        if (currentPoint.isAbnormal) {
          ctx.strokeStyle = '#FF3B30'; // 红色-异常
        } else if (currentPoint.isSafe) {
          ctx.strokeStyle = '#30D158'; // 绿色-安全
        } else {
          ctx.strokeStyle = color; // 默认颜色
        }
        
        ctx.beginPath();
        ctx.moveTo(currentPoint.x, currentPoint.y);
        ctx.lineTo(nextPoint.x, nextPoint.y);
        ctx.stroke();
      }
    }

    // Draw data points with status colors
    points.forEach((point, index) => {
      const animatedIndex = Math.floor(index * animationProgress);
      if (index <= animatedIndex) {
        // 选择点的颜色
        let pointColor = color;
        if (point.isAbnormal) {
          pointColor = '#FF3B30'; // 红色
        } else if (point.isSafe) {
          pointColor = '#30D158'; // 绿色
        }
        
        // White border
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
        ctx.fill();
        
        // Colored inner circle
        ctx.fillStyle = pointColor;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 2.5, 0, 2 * Math.PI);
        ctx.fill();
      }
    });

    // Label values directly on the curve
    ctx.font = '600 11px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'center';
    
    points.forEach((point, index) => {
      const animatedIndex = Math.floor(index * animationProgress);
      if (index <= animatedIndex) {
        // 选择文字颜色
        let textColor = '#1d1d1f';
        if (point.isAbnormal) {
          textColor = '#FF3B30';
        } else if (point.isSafe) {
          textColor = '#30D158';
        }
        
        // Value label background
        const text = `${point.value}`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width + 8;
        const textHeight = 16;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.beginPath();
        ctx.roundRect(
          point.x - textWidth/2, 
          point.y - textHeight - 10, 
          textWidth, 
          textHeight, 
          6
        );
        ctx.fill();
        
        // Value text with status color
        ctx.fillStyle = textColor;
        ctx.fillText(text, point.x, point.y - 14);
      }
    });

    // Draw horizontal axis time labels
    ctx.fillStyle = '#8e8e93';
    ctx.font = '400 11px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'center';
    
    points.forEach((point, index) => {
      const animatedIndex = Math.floor(index * animationProgress);
      if (index <= animatedIndex) {
        // 处理新的日期格式 "2024年X月" 或旧格式 "2024-XX"
        let dateStr = point.date;
        if (point.date.includes('-')) {
          // 旧格式: "2024-01" -> 第一个显示"24年1月"，其他显示"2月"
          const parts = point.date.split('-');
          const year = parts[0].slice(-2); // 取年份后两位
          const month = parseInt(parts[1]) + '月';
          dateStr = index === 0 ? `${year}年${month}` : month;
        } else if (point.date.includes('年') && point.date.includes('月')) {
          // 新格式: "2024年1月" -> 第一个显示"24年1月"，其他显示"2月"
          const parts = point.date.split('年');
          const year = parts[0].slice(-2); // 取年份后两位
          const month = parts[1];
          dateStr = index === 0 ? `${year}年${month}` : month;
        }
        ctx.fillText(dateStr, point.x, height - 6);
      }
    });

  }, [data, color, animationProgress, unit, thresholdValue, thresholdLabel]);

  // Add CanvasRenderingContext2D.roundRect polyfill (compatibility)
  useEffect(() => {
    if (typeof CanvasRenderingContext2D !== 'undefined' && !CanvasRenderingContext2D.prototype.roundRect) {
      CanvasRenderingContext2D.prototype.roundRect = function(x: number, y: number, width: number, height: number, radius: number) {
        this.beginPath();
        this.moveTo(x + radius, y);
        this.lineTo(x + width - radius, y);
        this.quadraticCurveTo(x + width, y, x + width, y + radius);
        this.lineTo(x + width, y + height - radius);
        this.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        this.lineTo(x + radius, y + height);
        this.quadraticCurveTo(x, y + height, x, y + height - radius);
        this.lineTo(x, y + radius);
        this.quadraticCurveTo(x, y, x + radius, y);
        this.closePath();
      };
    }
  }, []);

  return (
    <div className="trend-chart-container-clean">
      <canvas 
        ref={canvasRef} 
        className="trend-chart-canvas-clean"
        style={{ width: '100%', height: '140px' }}
      />
    </div>
  );
};

declare global {
  interface CanvasRenderingContext2D {
    roundRect(x: number, y: number, width: number, height: number, radius: number): void;
  }
}

export default TrendChart;